import { RpcError } from "grpc-web";
import { GetUserResponse } from "./../proto/api_pb.d";
import { useCallback, useEffect, useState } from "react";
import { OmmuseClient } from "../proto/api_grpc_web_pb";
import { GetUserRequest } from "../proto/api_pb";
import { backOff } from "exponential-backoff";
import { useAuth0 } from "@auth0/auth0-react";
import useGetValidAccessToken from "./useGetValidAccessToken";

type ReturnType = {
  isUserLoading: boolean;
  getUserResponse: GetUserResponse | undefined;
  getUserError: RpcError | undefined;
  emailVerified: boolean | undefined;
  refreshUserDetails: () => void;
};

export const useGetUser = (
  userId?: string,
  profileUpdated?: boolean,
  shareToken?: string,
): ReturnType => {
  const [getUserResponse, setGetUserResponse] = useState<GetUserResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [getUserError, setGetUserError] = useState<RpcError>();
  const { user } = useAuth0();
  const { getValidAccessToken } = useGetValidAccessToken();

  const getCurrentUser = useCallback(async () => {
    try {
      if (userId && userId !== "") {
        setIsLoading(true);
        setGetUserError(undefined)
        const accessToken = shareToken
          ? shareToken
          : await getValidAccessToken();
        const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
        const request = new GetUserRequest();
        request.setUserId(userId);
        const metadata = {
          Authorization: `${accessToken}`,
          "Access-Control-Allow-Origin": "*",
        };
        await backOff(
          () =>
            new Promise((resolve, reject) => {
              ommuseClient.getUser(request, metadata, function (err, response) {
                if (err) {
                  console.log(err.code);
                  console.log(err.message);
                  setIsLoading(false);
                  setGetUserResponse(undefined);
                  setGetUserError(err)
                  reject(err);
                } else {
                  setGetUserError(undefined);
                  setGetUserResponse(response);
                  setIsLoading(false);
                  resolve(response);
                }
              });
            }),
          {
            numOfAttempts: 2,
            jitter: "full",
            timeMultiple: 5,
          }
        );
      }
    } catch (error) {
      setGetUserResponse(undefined);
      setGetUserError(error as RpcError);
    }
  }, [userId, shareToken, getValidAccessToken]);

  useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser, profileUpdated]);

  const refreshUserDetails = useCallback(() => {
    getCurrentUser();
  }, [getCurrentUser]);


  return {
    isUserLoading: isLoading,
    getUserResponse,
    getUserError: getUserError,
    emailVerified: user ? user.email_verified : undefined,
    refreshUserDetails,
  };
};
